import React, { useMemo } from 'react';

import Check from '@rover/icons/svg/check.svg';
import { Box, Flex } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import useTheme from '@rover/kibble/themes/useTheme';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

type UnorderedListVariant = 'primary' | 'secondary' | 'interactive';

export type UnorderedListVariantMap<T> = {
  [P in UnorderedListVariant]: T;
};

const getColorMap = (colors: RoverTheme['colors']): UnorderedListVariantMap<string> => ({
  primary: colors.brand.active_brand,
  interactive: colors.interactive_link.primary_base,
  secondary: colors.neutral.black,
});

export type Props = Omit<BoxProps, 'webAs'> & {
  children: React.ReactNode;
  centered?: boolean;
  icon?: Icons;
  listVariant?: UnorderedListVariant;
};

type IconListItemProps = {
  children: React.ReactNode;
  icon: Icons;
  listVariant?: UnorderedListVariant;
};

export enum Icons {
  CheckMark,
}

type ListItemProps = Pick<BoxProps, 'ml' | 'sx' | 'mb'> & {
  webAs?: 'li';
  centered?: boolean;
  listVariant?: UnorderedListVariant;
  children: React.ReactNode;
};

export const ListItem: React.FC<ListItemProps> = ({
  centered = false,
  listVariant = 'primary',
  children,
  ...props
}) => {
  const { colors } = useTheme();

  return (
    <Box
      sx={{
        '&::marker': {
          color: getColorMap(colors)[listVariant],
        },
        ...(centered && { listStylePosition: 'inside' }),
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const IconListItem = ({
  icon,
  children,
  listVariant = 'primary',
}: IconListItemProps): JSX.Element => {
  const { colors } = useTheme();
  const iconResources = useMemo(
    () => ({
      [Icons.CheckMark]: (
        <Check
          data-qa-id="unordered-list-check"
          width="30px"
          height="30px"
          viewBox="4 4 24 24"
          fill={getColorMap(colors)[listVariant]}
        />
      ),
    }),
    [colors, listVariant]
  );

  return (
    <Flex webAs="li" mb="2x" flexDirection="row">
      <Flex justifyContent="center" minWidth="30px">
        {iconResources[icon]}
      </Flex>
      <ListItem ml="6x">{children}</ListItem>
    </Flex>
  );
};

const UnorderedList = ({ centered = false, ...props }: Props): JSX.Element => {
  let ulPadding;
  let paddingInlineStart;
  if (props.icon !== undefined) {
    ulPadding = 0;
    paddingInlineStart = 0;
  }
  if (props.listVariant === 'secondary') {
    ulPadding = 0;
    paddingInlineStart = '25px';
  }

  return (
    <Box
      sx={{
        textAlign: centered ? 'center' : undefined,
        padding: ulPadding,
        paddingInlineStart,
      }}
      pl={centered ? '0x' : undefined}
      {...props}
      webAs="ul"
    >
      {React.Children.map(props.children, (item) =>
        props.icon === undefined ? (
          <ListItem webAs="li" mb="2x" centered={centered} listVariant={props.listVariant}>
            {item}
          </ListItem>
        ) : (
          <IconListItem icon={props.icon} listVariant={props.listVariant}>
            {item}
          </IconListItem>
        )
      )}
    </Box>
  );
};

export default UnorderedList;
